/**
 * MUTAGIM.SHOP — Bundle Row (Phase 15)
 * Ligne Netflix pour les packs/bundles avec économies affichées
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Package, ChevronLeft, ChevronRight, ShoppingCart, Check } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BundleRow({ className }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const scrollRef = useRef(null);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingBundle, setAddingBundle] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const isRTL = i18n.dir() === 'rtl';
  const lang = i18n.language;

  // Charger les bundles
  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const res = await fetch(`${API_URL}/api/promotions/bundles`);
        if (res.ok) {
          const data = await res.json();
          setBundles(data.bundles || []);
        }
      } catch (err) {
        console.error('Error fetching bundles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBundles();
  }, []);

  // Navigation
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const effectiveScroll = isRTL ? -scrollLeft : scrollLeft;
      setCanScrollLeft(effectiveScroll > 10);
      setCanScrollRight(effectiveScroll < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, [bundles]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      const currentScroll = scrollRef.current.scrollLeft;
      const actualDirection = isRTL ? (direction === 'left' ? 'right' : 'left') : direction;
      const newScroll = actualDirection === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount;
      scrollRef.current.scrollTo({ left: newScroll, behavior: 'smooth' });
    }
  };

  // Ajouter tous les produits du bundle au panier
  const handleAddBundle = async (bundle, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setAddingBundle(bundle.id);
    try {
      // Ajouter chaque produit du bundle
      for (const product of bundle.products || []) {
        await addToCart(product.id);
      }
      toast.success(t('promotions.bundleAdded', 'Pack ajouté au panier !'));
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setAddingBundle(null);
    }
  };

  if (loading || bundles.length === 0) return null;

  return (
    <section 
      className={cn("relative group/row", className)}
      data-testid="bundle-row"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 px-4 py-2 rounded-lg">
            <Package className="w-5 h-5 text-white" />
            <span className="text-white font-bold text-lg">
              {t('promotions.bundles', 'Nos Packs')}
            </span>
          </div>
          <span className="text-gray-400 text-sm hidden md:block">
            {t('promotions.bundlesSavings', 'Économisez en achetant ensemble')}
          </span>
        </div>

        {/* Navigation */}
        <div className="hidden md:flex items-center gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">
          <Button
            variant="outline"
            size="icon"
            className={cn("border-gray-700 text-white hover:bg-purple-600 hover:border-purple-600", !canScrollLeft && "opacity-30")}
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
          >
            {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={cn("border-gray-700 text-white hover:bg-purple-600 hover:border-purple-600", !canScrollRight && "opacity-30")}
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
          >
            {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Bundles row */}
      <div className="relative">
        {canScrollLeft && (
          <div className={cn(
            "absolute top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-r from-mutagim-dark to-transparent",
            isRTL ? "right-0" : "left-0"
          )} />
        )}
        {canScrollRight && (
          <div className={cn(
            "absolute top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-l from-mutagim-dark to-transparent",
            isRTL ? "left-0" : "right-0"
          )} />
        )}

        <div
          ref={scrollRef}
          className={cn(
            "flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4",
            isRTL && "flex-row-reverse"
          )}
        >
          {bundles.map((bundle) => (
            <div
              key={bundle.id}
              className="flex-shrink-0 w-[320px] md:w-[380px] bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden border border-purple-500/30 hover:border-purple-500 transition-all group hover:shadow-lg hover:shadow-purple-500/20"
            >
              {/* Products grid */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {(bundle.products || []).slice(0, 4).map((product, idx) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.slug}`}
                      className="aspect-square bg-gray-800 rounded-lg overflow-hidden hover:ring-2 ring-purple-500 transition-all"
                    >
                      <img
                        src={product.image || '/placeholder-product.jpg'}
                        alt={product.name?.[lang] || ''}
                        className="w-full h-full object-cover"
                      />
                    </Link>
                  ))}
                </div>

                {/* Bundle info */}
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        {bundle.name?.[lang] || bundle.name?.fr || 'Pack'}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-2">
                        {bundle.description?.[lang] || bundle.description?.fr || `${bundle.products?.length || 0} produits`}
                      </p>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      -{bundle.savings_percent}%
                    </Badge>
                  </div>

                  {/* Pricing */}
                  <div className="flex items-center justify-between bg-black/30 rounded-lg p-3">
                    <div>
                      <p className="text-gray-500 text-xs line-through">
                        {formatPrice({ EUR: bundle.original_price })}
                      </p>
                      <p className="text-white font-bold text-xl">
                        {formatPrice({ EUR: bundle.bundle_price })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-semibold">
                        {t('promotions.youSave', 'Économisez')}
                      </p>
                      <p className="text-green-400 text-lg font-bold">
                        {formatPrice({ EUR: bundle.savings })}
                      </p>
                    </div>
                  </div>

                  {/* Add to cart button */}
                  <Button
                    onClick={(e) => handleAddBundle(bundle, e)}
                    disabled={addingBundle === bundle.id}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid={`add-bundle-${bundle.id}`}
                  >
                    {addingBundle === bundle.id ? (
                      <Check className="w-4 h-4 mr-2 animate-pulse" />
                    ) : (
                      <ShoppingCart className="w-4 h-4 mr-2" />
                    )}
                    {t('promotions.addBundle', 'Ajouter le pack')}
                  </Button>

                  {/* Products included */}
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Check className="w-3 h-3" />
                    <span>
                      {(bundle.products || []).slice(0, 3).map(p => p.name?.[lang] || p.name?.fr).join(', ')}
                      {(bundle.products?.length || 0) > 3 && ` +${bundle.products.length - 3}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default BundleRow;
